import React, { FC, useEffect, useRef, useState } from "react";
import {
	Box,
	Button,
	IconButton,
	Paper,
	SxProps,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { AddOutlined, Delete, EditOutlined } from "@mui/icons-material";

import {ESRAggregateFieldProps, ESRAggregateFieldRow, ESRState} from "../types/data";
import { ESRQuestion } from "./ESRQuestion";
import { ESRQuestionLabel } from "./ESRQuestionLabel";
import {useDispatch, useSelector} from "react-redux";

import { deleteRow } from "../actions/form";
import { useParams } from "react-router-dom";

const rowIdCellStyle: SxProps = {
	position: "sticky",
	left: 0,
	zIndex: 1,
	background: "#E7EFFA",
	"&::after": {
		content: '""',
		height: "100%",
		width: "1px",
		background: "lightgray",
		position: "absolute",
		right: 0,
		top: 0,
		bottom: 0,
	},
};

type TableRowProps = {
	question: ESRAggregateFieldProps;
	row: ESRAggregateFieldRow;
	rowIndex: number;
	tableEditMode: boolean;
};

const ESRTableRow: FC<TableRowProps> = ({ question, row, rowIndex, tableEditMode }) => {
	const { activeId = "" } = useParams();
	const { question_id, columns, locked, deletable } = question;
	const dispatch = useDispatch();
	const extraRowRef = useRef<HTMLElement | null>(null);
	const responses = useSelector((state: ESRState) => state.responses);
	let isSelectedRow: boolean = false;
	if(question_id === "E14T"){
		// Determine if the row is the selected row
		isSelectedRow = columns.some((col) => {
			const cell = row.columns[col?.question_id];
			if (!cell?.question_id) return false;
			const row_index = cell.question_id.split(".")[1];
			return (row_index === (responses["E14T.SELECTED_ROW"]?.response ?? ""));
		});
	}
	return (
		<>
			<TableRow
				key={"input-" + rowIndex}
				sx={{
					backgroundColor: isSelectedRow ? "#c8e6c9" : "inherit",
					borderLeft: isSelectedRow
						? "6px solid #1bae3b"
						: "6px solid transparent",
					transform: isSelectedRow ? "translateY(-2px)" : "none",
					boxShadow: isSelectedRow ? "0 4px 12px rgba(0,0,0,0.15)" : "none",
					transition: "all 0.2s ease",
					"& td": {
						backgroundColor: isSelectedRow ? "#a8e89f" : "inherit",
						fontWeight: isSelectedRow ? 600 : 400,
					},
				}}
			>
				<TableCell component="td" sx={rowIdCellStyle}>
					<Typography
						sx={{
							mx: "4px",
							fontFamily: "Roboto",
							fontSize: "14px",
							fontWeight: isSelectedRow? 700: 500,
						}}
					>
						{rowIndex + 1}
					</Typography>
				</TableCell>
				{columns.map((column, index) => (
					<TableCell
						key={`${index}-${column.question_id}`}
						component="td"
						sx={{ minWidth: 200 }}
					>
						{row.columns[column.question_id] ? (
							<ESRQuestion
								key={`${index}-${column.question_id}`}
								{...row.columns[column.question_id]}
								inline={true}
								portalContainerEl={extraRowRef.current}
								tableEditMode={tableEditMode}
							/>
						) :
							(column.field_type != 'header' &&  column.field_type != 'label')? (
								<Box
								sx={{
									width: '100%',
									height: '100%',
									backgroundColor: 'secondary',
									minHeight: 40
								}}
							><i>N/A</i></Box>
							) : <></>
						}
					</TableCell>
				))}
				<TableCell>
					{
						<IconButton
						disabled={deletable>0}
						aria-label={`delete row ${row.id}`}
						onClick={() => {
							dispatch(deleteRow(activeId, question_id, row.id));
						}}
						>
							{
								locked === 0 && 
								<Delete 
									style={{ color: deletable > 0 ? "#9e9e9e" : undefined }}
      								color={deletable > 0 ? undefined : "error"}
								/>
							}
						</IconButton>
					}
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell
					colSpan={columns.length + 2 /* ID & delete button */}
					ref={extraRowRef}
					sx={{ padding: 0 }}
				></TableCell>
			</TableRow>
		</>
	);
};

export const ESRAggregateField = (props: ESRAggregateFieldProps) => {
	const { rows, columns, singleRow, locked, deletable } = props;
	const [emptyRowHidden, setEmptyRowHidden] = useState(true);
	const [tableEditMode, setTableEditMode] = useState(false);
	// locked === 3 means an Admin granted this user permission to edit the table
	const isGranted = locked === 3;
	const canAddRows = locked <= 1 || isGranted;

	useEffect(() => {
		setEmptyRowHidden(true);
	}, [rows.length]);

	return (
		<>
			<TableContainer 
				component={Paper} 
				sx={{
					maxWidth: "100%",
					maxHeight: "70vh",      
					overflow: "auto",

				}}
			>
				<Table stickyHeader sx={{ background: "#E7EFF8" }}>
					<TableHead>
						<TableRow>
							<TableCell component="th" scope="col" sx={rowIdCellStyle}>
								{(locked === 1 || isGranted) && !tableEditMode ? (
									<IconButton
										size="small"
										aria-label="enable table edit mode"
										onClick={() => setTableEditMode(true)}
										sx={{
											background: "#1976d2",
											"&:hover": { background: "#1565c0" },
											width: 28,
											height: 28,
										}}
									>
										<EditOutlined sx={{ fontSize: 16, color: "#fff" }} />
									</IconButton>
								) : (
									<Typography sx={{ mx: "4px", fontFamily: "Roboto", fontSize: "14px", fontWeight: 500 }}>
										ID
									</Typography>
								)}
							</TableCell>
							{columns.map((column, index) => (
								<TableCell key={`${index}-${column.question_id}`} component="th" scope="col" sx={{ backgroundColor: "#E7EFF8" }}>
									<ESRQuestionLabel {...column} inline={true} />
								</TableCell>
							))}
							<TableCell component="th" scope="col" sx={{ backgroundColor: "#E7EFF8" }}/>
						</TableRow>
					</TableHead>
					<TableBody>
						{((canAddRows && !emptyRowHidden) || singleRow ? rows : rows.slice(0, -1)).map(
							(row, index) => (
								<ESRTableRow key={index} question={props} row={row} rowIndex={index} tableEditMode={tableEditMode}/>
							),
						)}
					</TableBody>
				</Table>
			</TableContainer>
			{ canAddRows && emptyRowHidden ? (
				<Box key="hidden">
					<Button
						sx={{
							w: 64,
							h: 64,
							my: "10px",
							borderRadius: "32px",
							background: "#398DEF14",
						}}
						onClick={() => setEmptyRowHidden(false)}
					>
						<AddOutlined />
						{singleRow ? <>Show Form</> : (canAddRows)?<>Add Entry</>:<></>}
					</Button>
				</Box>
			) : null}
		</>
	);
};
